import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setSearchTerm } from "../store/features/searchSlice";
import SmokeBackground from "./SmokeBackground";
import { CountBadge, Input } from "./ui";
import { trackEvent } from "../analytics";

import {
  HomeIcon,
  ShoppingBagIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  HeartIcon,
  UserIcon,
} from "@heroicons/react/24/outline";

const MANAGERS = [
  { label: "📣 Менеджер", url: "https://t.me/tasja_1212_2" },
  { label: "💬 Менеджер", url: "https://t.me/Hostiev22" },
];

const openManager = (url) => {
  trackEvent("ContactManagerClicked", { manager: url.split("/").pop() });
  const tg = window.Telegram?.WebApp;
  if (tg?.openTelegramLink) {
    tg.openTelegramLink(url);
  } else {
    window.open(url, "_blank");
  }
};

const Layout = () => {
  const cartCount = useSelector((state) => state.cart.items.length);
  const favoritesCount = useSelector((state) => state.favorites.items.length);
  const searchTerm = useSelector((state) => state.search.term);
  const dispatch = useDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === "/";

  const navItems = [
    { to: "/", label: "Главная", icon: HomeIcon },
    { to: "/cart", label: "Корзина", icon: ShoppingBagIcon, count: cartCount },
    { to: "/favorites", label: "Избранное", icon: HeartIcon, count: favoritesCount },
    { to: "/profile", label: "Профиль", icon: UserIcon },
  ];

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  // isolate: дим (z-index: -1) малюється над фоном, але під усім вмістом —
  // без z-index у main, тож фіксовані панелі сторінок (z-50) лишаються над нижнім меню (z-40)
  return (
    <div className="relative isolate min-h-screen bg-[#101622] text-slate-100 pb-28 overflow-x-hidden">
      <SmokeBackground />

      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-[#101622]/80 backdrop-blur-md border-b border-slate-800 px-4 pb-3 pt-[calc(env(safe-area-inset-top)+12px)]">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <span className="text-lg font-extrabold tracking-tight">
            SMAK<span className="text-blue-500">.</span>BAR
          </span>

          <div className="flex gap-2">
            {MANAGERS.map((m) => (
              <button
                key={m.url}
                onClick={() => openManager(m.url)}
                className="text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-800 border border-slate-600 hover:bg-slate-700 hover:border-slate-500 transition"
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {isHome && (
          <div className="mt-3 max-w-6xl mx-auto">
            <Input
              type="search"
              icon={MagnifyingGlassIcon}
              placeholder="Поиск товаров..."
              aria-label="Поиск товаров"
              // Пошук не скидається при переході на товар — повернувшись, людина бачить той самий список
              value={searchTerm}
              onChange={(e) => dispatch(setSearchTerm(e.target.value))}
            />
          </div>
        )}
      </header>

      {!isHome && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <button
            onClick={goBack}
            className="flex items-center gap-2 text-slate-300 hover:text-white font-medium transition"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Назад
          </button>
        </div>
      )}

      <main className="max-w-6xl mx-auto p-2">
        <Outlet />
      </main>

      {/* BOTTOM NAVIGATION */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-[#101622]/95 backdrop-blur-xl border-t border-slate-800 px-6 pt-3 pb-[calc(env(safe-area-inset-bottom)+12px)] flex justify-between items-center">
        {navItems.map(({ to, label, icon: Icon, count }) => {
          const active = location.pathname === to;
          return (
            <Link
              key={to}
              to={to}
              className={`flex flex-col items-center gap-0.5 text-xs font-medium transition ${
                active ? "text-blue-400" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              <span className="relative">
                <Icon className="w-6 h-6" />
                <CountBadge count={count} />
              </span>
              {label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default Layout;
